
import React from 'react';
import { ModalCampaignQuestion } from '@/types/modalCampaigns';
import QuizQuestion from './components/QuizQuestion';
import QuizResultMessage from './components/QuizResultMessage';
import QuizEmptyState from './components/QuizEmptyState';
import QuizAttemptInfo from './components/QuizAttemptInfo';
import { useAuth } from '@/context/AuthContext';

interface QuizContentProps {
  questions: ModalCampaignQuestion[];
  userAnswers: Record<string, string[]>;
  quizSubmitted: boolean;
  quizPassed: boolean;
  score: number;
  minScore: number;
  attemptNumber: number;
  maxAttempts: number | null;
  attemptsExhausted: boolean;
  currentQuestionIndex: number;
  onAnswer: (questionId: string, selectedOptions: string[]) => void;
}

const QuizContent: React.FC<QuizContentProps> = ({
  questions,
  userAnswers,
  onAnswer,
  quizSubmitted,
  quizPassed,
  score,
  minScore,
  attemptNumber,
  maxAttempts,
  attemptsExhausted,
  currentQuestionIndex
}) => {
  // Get the current user
  const { user } = useAuth();
  
  // Calculate total available points
  const totalAvailablePoints = questions.reduce((sum, q) => sum + q.points, 0);
  
  const handleOptionChange = (questionId: string, option: string, checked: boolean) => {
    const currentAnswers = userAnswers[questionId] || [];
    
    let updatedAnswers: string[];
    if (checked) {
      // Add option if checked
      updatedAnswers = [...currentAnswers, option];
    } else {
      // Remove option if unchecked
      updatedAnswers = currentAnswers.filter(o => o !== option);
    }
    
    onAnswer(questionId, updatedAnswers);
  };

  if (quizSubmitted) {
    return (
      <div className="space-y-4">
        <QuizResultMessage 
          quizPassed={quizPassed}
          score={score}
          totalAvailablePoints={totalAvailablePoints}
          attemptNumber={attemptNumber}
          maxAttempts={maxAttempts}
          attemptsExhausted={attemptsExhausted}
          user={user}
        />
      </div>
    );
  }

  // If there are no questions or the current question doesn't exist
  if (questions.length === 0 || !questions[currentQuestionIndex]) {
    return <QuizEmptyState questionsLength={questions.length} />;
  }
  
  // Display current question
  const currentQuestion = questions[currentQuestionIndex];
  
  return (
    <div className="space-y-6">
      <QuizAttemptInfo 
        attemptNumber={attemptNumber}
        maxAttempts={maxAttempts}
        totalAvailablePoints={totalAvailablePoints}
      />
      
      <QuizQuestion 
        question={currentQuestion}
        questionIndex={currentQuestionIndex}
        userAnswers={userAnswers[currentQuestion.id] || []}
        onAnswerChange={handleOptionChange}
      />
    </div>
  );
};

export default QuizContent;
